/* eslint-disable no-restricted-syntax */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useSetRecoilState } from 'recoil';

import RoundExclamationMark from '../assets/images/roundExclamationMark.svg';
import SortIcon from '../assets/images/sortIcon.svg';
import { ErrorPage } from '../components/ErrorInterrupter';
import { SearchInput } from '../components/input/SearchInput';
import { NoData } from '../components/NoData';
import { LoadingFallback } from '../components/progress/LoadingFallback';
import type { Deck } from '../model/domain/Deck';
import { useAsyncState } from '../model/state/asyncState';
import { notificationState } from '../model/state/errorState';
import StudyService from '../service/StudyService';
import { analytics } from '../util/analytics/AnalyticsController';

export enum SortOrder {
  nameForward,
  nameReverse,
  tklForward,
  tklReverse,
}

interface SortItemProps {
  name: string;
  onClick: () => void;
  disabled?: boolean;
}

export const SortItem: React.FC<SortItemProps> = ({ name, onClick, disabled = false }) => {
  return (
    <button
      disabled={disabled}
      className="cursor-pointer border-none bg-transparent"
      type="button"
      onClick={onClick}
    >
      <div className="flex flex-row items-center font-Lato">
        <p className="mr-[5px] font-bold">{name}</p>
        <img alt="sort" src={SortIcon} />
      </div>
    </button>
  );
};

interface HeadProps {
  title: string;
  text: string;
}

export const Head: React.FC<HeadProps> = ({ title, text }) => {
  return (
    <div className="font-Roboto">
      <h1 className="m-0 text-[25px] font-bold leading-[29px] text-black">{title}</h1>
      <p className="m-0 mt-[3px] text-[18px] font-normal leading-[23px] text-black">{text}</p>
    </div>
  );
};

interface DeckItemProps {
  deck: Deck;
}

const DeckItem: React.FC<DeckItemProps> = ({ deck }) => {
  const onClick = useCallback(() => {
    analytics.libraryModeSelectDeck(deck.deckName);
  }, [deck.deckName]);

  return (
    <Link
      className="text-[#2D3540] no-underline"
      to={`/library/deck/${deck.deckId}`}
      onClick={onClick}
    >
      <div
        className={`flex flex-row items-center justify-between px-[21px] pb-[2px] pt-[3px] shadow-[0px_1px_0px_0px_#0000001A] hover:pb-[3px] hover:shadow-[0px_2px_0px_0px_#0000001A]`}
      >
        <div className="m-0 text-[#2D3540]">
          <p className="my-[10px]">{deck.deckName}</p>
        </div>
        <p className="m-0 text-[#2D3540]">{deck.tkl === null ? 'N/A' : `${deck.tkl}%`}</p>
      </div>
    </Link>
  );
};

const DeckList: React.FC = () => {
  const state = useAsyncState(StudyService.getUserLibraryDecks);
  const [sort, setSort] = useState<SortOrder>(SortOrder.nameForward);
  const [searchString, setSearchString] = useState('');

  const searchResult = useMemo(() => {
    if (!state.data) return [];
    if (typeof searchString === 'undefined' || searchString === '') return state.data;

    return state.data.filter((card) => {
      const id = card.deckId.toLowerCase();
      const name = card.deckName.toLowerCase();
      const search = searchString.toLowerCase();

      return id.includes(search) || name.includes(search);
    });
  }, [state, searchString]);

  const sortedDecks = useMemo(() => {
    if (!searchResult) return [];
    const mutable = [...searchResult];

    return mutable.sort((a, b) => {
      const deckAName = a.deckName.toLowerCase();
      const deckBName = b.deckName.toLowerCase();
      const tklA = a.tkl || 0;
      const tklB = b.tkl || 0;
      switch (sort) {
        case SortOrder.nameForward:
          return deckAName > deckBName ? 1 : -1;
        case SortOrder.nameReverse:
          return deckAName > deckBName ? -1 : 1;
        case SortOrder.tklForward:
          return tklA - tklB;
        case SortOrder.tklReverse:
          return tklB - tklA;
      }
    });
  }, [searchResult, sort]);

  const setNotification = useSetRecoilState(notificationState);

  useEffect(() => {
    analytics.libraryModePageView();
    document.title = 'Library Deck List – Blank Slate';
  }, []);

  if (state.error) return <ErrorPage error={state.error} onReset={() => {}} />;

  return (
    <div className="h-full w-full">
      <div className="flex w-full flex-col justify-between px-10 font-Lato text-[14px] lg:px-0 lg:pb-7 lg:pl-[44px] lg:pr-[48px]">
        <Head title="Content Decks" text="Click on any deck to review" />
        <div className="mt-[28px]">
          <SearchInput
            isDisabled={state.inProgress}
            onChange={(newValue) => {
              setSearchString(newValue);
            }}
            value={searchString}
            placeholder="search"
          />
        </div>
        {/*{resourcesState.data && resourcesState.data.length ? (*/}
        {/*  <Link to="/library/resources" className={classes.resourcesButton}>*/}
        {/*    <p className={classes.resourcesButtonText}>Extra Resources</p>*/}
        {/*  </Link>*/}
        {/*) : null}*/}

        {state.inProgress ? (
          <LoadingFallback />
        ) : (
          <div className="mb-[18px] mt-3 rounded-[12px] bg-white shadow-[0px_4px_4px_rgba(0,0,0,0.25)] lg:mb-0 lg:mt-[18px] lg:px-[28px] lg:pb-[33px] lg:pt-[29px]">
            <div className="flex flex-row justify-between rounded-t-[12px] bg-[#E0E0E066] px-[21px] py-[9px] lg:rounded-[12px]">
              <SortItem
                disabled={state.inProgress}
                onClick={() => {
                  if (sort === SortOrder.nameForward) {
                    setSort(SortOrder.nameReverse);
                  } else {
                    setSort(SortOrder.nameForward);
                  }
                }}
                name="Deck"
              />
              <div className="flex cursor-pointer flex-row items-center justify-center gap-[6px]">
                <div
                  role="button"
                  className="h-[18px] w-[18px]"
                  onClick={() => {
                    setNotification(
                      'TKL stands for True Knowledge Level™. This is Blank Slate’s unique analytic that estimates how well you know all of your material in this moment. In other words, if you were to take a pop quiz on all of your material right now, your TKL is the score you would be predicted to achieve.'
                    );
                  }}
                >
                  <img className="h-[18px] w-[18px]" alt="hint" src={RoundExclamationMark} />
                </div>

                <SortItem
                  disabled={state.inProgress}
                  onClick={() => {
                    if (sort === SortOrder.tklForward) {
                      setSort(SortOrder.tklReverse);
                    } else {
                      setSort(SortOrder.tklForward);
                    }
                  }}
                  name="TKL"
                />
              </div>
            </div>
            <div className="h-full max-h-[calc(100svh-330px)] w-full overflow-y-auto px-[2px] pb-[2px] pr-[12px]">
              {sortedDecks.length === 0 ? (
                <NoData />
              ) : (
                sortedDecks.map((deck) => <DeckItem key={deck.deckId} deck={deck} />)
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeckList;
